/**
 * PatientDropdown — Reusable alphabetical searchable patient selector
 *
 * Props:
 *   patients       - Array of { id, first_name, last_name, mrn, ... }
 *   value          - Currently selected patient ID (string or number)
 *   onChange        - Callback(patientId: string) when selection changes
 *   placeholder     - Placeholder text (default: "— Choose a patient —")
 *   disabled        - Disable the dropdown
 *   idPrefix        - Unique prefix for DOM IDs to avoid collisions (default: "pd")
 *   style           - Optional style override for the outer wrapper
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { matchesNameQuery } from '../lib/nameSearch';

export default function PatientDropdown({
  patients = [],
  value,
  onChange,
  placeholder = '— Choose a patient —',
  disabled = false,
  idPrefix = 'pd',
  style,
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sort + filter + group by letter
  const grouped = useMemo(() => {
    const sorted = [...patients].sort((a, b) => {
      const la = (a.last_name || '').toLowerCase();
      const lb = (b.last_name || '').toLowerCase();
      if (la !== lb) return la.localeCompare(lb);
      return (a.first_name || '').toLowerCase().localeCompare((b.first_name || '').toLowerCase());
    });

    const term = search.toLowerCase().trim();
    // All-tokens name match (card CRM-FULLNAME-SEARCH). The explicit
    // "Last, First" arm this replaced is now redundant — the comma is a
    // token separator, so "Bubba, Joe" and "Joe Bubba" are the same query.
    const filtered = term
      ? sorted.filter(p => matchesNameQuery([p.last_name, p.first_name, p.mrn], term))
      : sorted;

    const groups = {};
    for (const p of filtered) {
      const letter = (p.last_name || p.first_name || '#')[0].toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(p);
    }
    return { filtered, groups, letters: Object.keys(groups).sort() };
  }, [patients, search]);

  // Display text for the selected value
  const displayText = useMemo(() => {
    const p = patients.find(pt => String(pt.id) === String(value));
    if (!p) return '';
    return `${p.last_name || ''}, ${p.first_name || ''} — MRN: ${p.mrn || p.id}`;
  }, [patients, value]);

  const select = (id) => {
    onChange(String(id));
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      {/* Trigger */}
      <div
        onClick={() => !disabled && setOpen(!open)}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          opacity: disabled ? 0.6 : 1,
          minHeight: 38, userSelect: 'none',
          background: '#fff', border: '1px solid #d1d5db', borderRadius: '0.375rem',
          padding: '0.5rem 0.75rem', fontSize: '0.875rem',
        }}
      >
        <span style={{
          color: value ? '#111827' : '#9ca3af',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
        }}>
          {displayText || placeholder}
        </span>
        <ChevronDown size={16} color="#6b7280" style={{
          flexShrink: 0, marginLeft: 8,
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
        }} />
      </div>

      {/* Dropdown panel */}
      {open && !disabled && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: '#fff', borderRadius: '0.5rem', marginTop: 4,
          boxShadow: '0 10px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.08)',
          border: '1px solid #e5e7eb', maxHeight: 360, display: 'flex', flexDirection: 'column',
        }}>
          {/* Search bar */}
          <div style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Search size={14} color="#9ca3af" />
            <input
              type="text"
              placeholder="Search by name or MRN..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
              style={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.875rem', background: 'transparent', color: '#111827' }}
              onClick={e => e.stopPropagation()}
            />
            {search && (
              <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{grouped.filtered.length} results</span>
            )}
          </div>

          {/* Letter quick-jump bar */}
          {grouped.letters.length > 3 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 2, padding: '0.35rem 0.5rem',
              borderBottom: '1px solid #f3f4f6', background: '#f9fafb',
            }}>
              {grouped.letters.map(letter => (
                <button
                  key={letter}
                  onClick={e => {
                    e.stopPropagation();
                    const el = document.getElementById(`${idPrefix}-grp-${letter}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  style={{
                    background: 'none', border: '1px solid #e5e7eb', borderRadius: 3,
                    padding: '1px 6px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                    color: '#2563eb', lineHeight: 1.4,
                  }}
                >
                  {letter}
                </button>
              ))}
            </div>
          )}

          {/* Scrollable grouped list */}
          <div style={{ overflowY: 'auto', maxHeight: 270 }}>
            {grouped.filtered.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                {patients.length === 0 ? 'No patients found' : 'No matches for search'}
              </div>
            ) : (
              grouped.letters.map(letter => (
                <div key={letter} id={`${idPrefix}-grp-${letter}`}>
                  <div style={{
                    padding: '0.25rem 0.75rem', background: '#f3f4f6',
                    fontSize: '0.7rem', fontWeight: 700, color: '#6b7280',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    position: 'sticky', top: 0, zIndex: 1,
                  }}>
                    {letter}
                  </div>
                  {grouped.groups[letter].map(p => (
                    <div
                      key={p.id}
                      onClick={() => select(p.id)}
                      style={{
                        padding: '0.5rem 0.75rem', cursor: 'pointer',
                        fontSize: '0.85rem', color: '#111827',
                        background: String(p.id) === String(value) ? '#eff6ff' : 'transparent',
                        borderLeft: String(p.id) === String(value) ? '3px solid #2563eb' : '3px solid transparent',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}
                      onMouseEnter={e => { if (String(p.id) !== String(value)) e.currentTarget.style.background = '#f9fafb'; }}
                      onMouseLeave={e => { if (String(p.id) !== String(value)) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span><strong>{p.last_name || ''}</strong>{p.last_name && p.first_name ? ', ' : ''}{p.first_name || ''}</span>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>MRN: {p.mrn || p.id}</span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
